import {Alert, Box, Chip, Grid, IconButton, InputAdornment, LinearProgress, TextField, Tooltip, Typography} from '@mui/material'
import {useMemo, useState} from 'react'
import SearchIcon from '@mui/icons-material/Search'
import {formatNumber} from '../../common/util'
import _ from 'lodash'
import Loader from '../../common/Loader'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import useOverviewHook from './useOverviewHook'
import {getAccess} from '../../helpers/ReduxHelpers'
import StorefrontIcon from '@mui/icons-material/Storefront'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip,
  Legend, ResponsiveContainer,
} from 'recharts'

// ─── Colour palette ────────────────────────────────────────────────────────────
const CHART_COLORS = ['#1565c0','#2e7d32','#6a1b9a','#e65100','#0277bd','#558b2f','#ad1457','#00695c','#f9a825','#4527a0']

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({icon, label, value, sub, color = '#1565c0', bg = '#e3f2fd', info}) {
  return (
    <Box sx={{
      borderRadius: 2.5,
      border: '1px solid #e8e8e8',
      backgroundColor: '#fff',
      p: 2,
      height: '100%',
      display: 'flex',
      gap: 1.5,
      alignItems: 'flex-start',
      boxShadow: 'none',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2,
        backgroundColor: bg, border: `1px solid ${color}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Typography sx={{color: '#9e9e9e', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1}}>
            {label}
          </Typography>
          {info && (
            <Tooltip arrow placement='top' title={
              <Box sx={{p: 0.5, maxWidth: 260}}>
                <Typography sx={{fontWeight: 700, fontSize: '0.72rem', mb: 0.5}}>How is this calculated?</Typography>
                <Typography sx={{fontSize: '0.7rem', lineHeight: 1.5}}>{info}</Typography>
              </Box>
            }>
              <IconButton size='small' sx={{p: 0.25, color: '#bdbdbd', '&:hover': {color: '#1565c0'}}}>
                <InfoOutlinedIcon sx={{fontSize: 14}} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography sx={{fontWeight: 800, fontSize: '1.6rem', color: '#1a1a1a', lineHeight: 1.1, mt: 0.4}}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{color: '#9e9e9e', fontSize: '0.7rem', mt: 0.3}}>{sub}</Typography>
        )}
      </Box>
    </Box>
  )
}

// ─── Section Header ────────────────────────────────────────────────────────────

function SectionHeader({title, sub, badge, info}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0'}}>
      <Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Typography sx={{fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a'}}>{title}</Typography>
          {info && (
            <Tooltip arrow placement='top' title={
              <Box sx={{p: 0.5, maxWidth: 280}}>
                <Typography sx={{fontWeight: 700, fontSize: '0.72rem', mb: 0.5}}>About this table</Typography>
                <Typography sx={{fontSize: '0.7rem', lineHeight: 1.5}}>{info}</Typography>
              </Box>
            }>
              <IconButton size='small' sx={{p: 0.25, color: '#bdbdbd', '&:hover': {color: '#1565c0'}}}>
                <InfoOutlinedIcon sx={{fontSize: 14}} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {sub && <Typography sx={{color: '#9e9e9e', fontSize: '0.75rem', mt: 0.2}}>{sub}</Typography>}
      </Box>
      {badge && (
        <Chip label={badge} size='small' sx={{height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1565c0', flexShrink: 0}} />
      )}
    </Box>
  )
}

// ─── Chart Card wrapper ────────────────────────────────────────────────────────

function ChartCard({title, sub, children, badge}) {
  return (
    <Box sx={{backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)'}}>
      <SectionHeader title={title} sub={sub} badge={badge} />
      <Box sx={{p: 2.5}}>{children}</Box>
    </Box>
  )
}

// ─── Table Section with search + pagination ────────────────────────────────────

function TableSection({title, sub, badge, info, columns, rows, nameKey = 'merchantName', exportToExcel = true}) {
  const [search, setSearch]     = useState('')
  const [page, setPage]         = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const name = (r[nameKey] || '').toLowerCase()
      return name === 'totals' || name.includes(q)
    })
  }, [rows, search, nameKey])

  return (
    <Box sx={{backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)'}}>
      <SectionHeader title={title} sub={sub} badge={badge} info={info} />
      <Box sx={{px: 2, py: 1.25, borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafbfc', display: 'flex', alignItems: 'center', gap: 2}}>
        <TextField
          size='small'
          placeholder={`Search by ${nameKey === 'merchantName' ? 'merchant' : 'courier'}…`}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0) }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{fontSize: 17, color: '#9e9e9e'}} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 280,
            '& .MuiOutlinedInput-root': {borderRadius: 2, fontSize: '0.82rem'},
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#1565c0'},
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#1565c0'},
          }}
        />
        {search && (
          <Typography sx={{fontSize: '0.75rem', color: '#9e9e9e'}}>
            {filtered.filter((r) => r[nameKey] !== 'Totals').length} result{filtered.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
      <ProzoDataGrid
        exportToExcel={exportToExcel}
        rowHeight={48}
        columns={columns}
        rows={filtered}
        /* Identity comes from rowKey (the merchant/courier id the row was grouped by), falling back
           to the display name only for row shapes that carry no id. Keying off the name alone gave
           duplicate ids whenever two ids share a display name, which MUI resolves unpredictably.
           The old Math.random() fallback was worse still: a fresh id every render, so React could
           never reconcile those rows. */
        getRowId={(row) => row.rowKey ?? row[nameKey] ?? row.id}
        rowCount={filtered.length}
        paginationMode='client'
        pagination
        page={page}
        setPage={setPage}
        sizePerPage={pageSize}
        setSizePerPage={(s) => { setPageSize(s); setPage(0) }}
      />
    </Box>
  )
}

// ─── Recharts custom tooltip ───────────────────────────────────────────────────

function ChartTooltip({active, payload, label, prefix = ''}) {
  if (!active || !payload?.length) return null
  return (
    <Box sx={{backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1.5, px: 1.5, py: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 140}}>
      <Typography sx={{fontSize: '0.72rem', fontWeight: 700, color: '#757575', mb: 0.5}}>{label}</Typography>
      {payload.map((p) => (
        <Box key={p.name} sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
          <Typography sx={{fontSize: '0.75rem', color: p.color, fontWeight: 600}}>{p.name}</Typography>
          <Typography sx={{fontSize: '0.75rem', fontWeight: 700, color: '#1a1a1a'}}>{prefix}{formatNumber(Number(p.value).toFixed(0), 0)}</Typography>
        </Box>
      ))}
    </Box>
  )
}

// ─── Visual Total Cell ─────────────────────────────────────────────────────────

function TotalCell({value, maxVal, grandTotal, isTotalsRow, prefix = ''}) {
  if (isTotalsRow) {
    return (
      <Chip
        label={`${prefix}${formatNumber(value?.toFixed(0), 0)}`}
        size='small'
        sx={{fontWeight: 800, fontSize: '0.75rem', height: 22, backgroundColor: '#1565c0', color: '#fff'}}
      />
    )
  }
  const barPct   = maxVal > 0 ? Math.min((value / maxVal) * 100, 100) : 0
  const sharePct = grandTotal > 0 ? ((value / grandTotal) * 100).toFixed(1) : '0.0'

  return (
    <Box sx={{width: '100%', px: 1}}>
      <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4}}>
        <Typography sx={{fontWeight: 700, fontSize: '0.82rem', color: '#1a1a1a'}}>
          {prefix}{formatNumber(value?.toFixed(0), 0)}
        </Typography>
        <Typography sx={{fontSize: '0.65rem', color: '#9e9e9e', fontWeight: 600}}>{sharePct}%</Typography>
      </Box>
      <LinearProgress
        variant='determinate'
        value={barPct}
        sx={{
          height: 4, borderRadius: 2,
          backgroundColor: '#f0f0f0',
          '& .MuiLinearProgress-bar': {
            borderRadius: 2,
            backgroundColor: barPct > 60 ? '#1565c0' : barPct > 30 ? '#42a5f5' : '#90caf9',
          },
        }}
      />
    </Box>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function OverviewCT({dateRange, mode, shipmentType}) {
  const accessList = getAccess()
  const {
    loading,
    finalMerchantDateData,
    finalCourierDateData,
    finalMerchantShipmentCountData,
    finalCourierShipmentCountData,
  } = useOverviewHook({mode, dateRange, shipmentType})

  // ── Derived KPIs ─────────────────────────────────────────────────────────────
  const totalRevenue      = _.find(finalMerchantDateData, {merchantName: 'Totals'})?.total || 0
  const totalShipments    = _.find(finalMerchantShipmentCountData, {merchantName: 'Totals'})?.total || 0
  const totalCourierShipments = _.find(finalCourierShipmentCountData, {courierName: 'Totals'})?.total || 0
  const totalCourierRevenue   = _.find(finalCourierDateData, {courierName: 'Totals'})?.total || 0
  const activeMerchants   = (finalMerchantDateData?.length || 1) - 1
  const avgRevPerShipment = totalShipments > 0 ? totalRevenue / totalShipments : 0
  const activeCouriers    = (finalCourierDateData?.length || 1) - 1

  const merchantRows   = (finalMerchantShipmentCountData || []).filter((r) => r.merchantName !== 'Totals')
  const revenueRows    = (finalMerchantDateData || []).filter((r) => r.merchantName !== 'Totals')
  const courierRows    = (finalCourierShipmentCountData || []).filter((r) => r.courierName !== 'Totals')
  const courierRevRows = (finalCourierDateData || []).filter((r) => r.courierName !== 'Totals')

  const maxMerchantShipments = _.maxBy(merchantRows, 'total')?.total || 1
  const maxMerchantRevenue   = _.maxBy(revenueRows,  'total')?.total || 1
  const maxCourierShipments  = _.maxBy(courierRows,  'total')?.total || 1
  const maxCourierRevenue    = _.maxBy(courierRevRows,'total')?.total || 1

  const topMerchant = _.maxBy(merchantRows, 'total')

  // ── Combined rows for Today's Overview ───────────────────────────────────────
  const combinedRows = useMemo(() => {
    // rowKey, not the name, for the same reason as the other tables — two merchant ids can share a
    // display name, and a duplicate row id makes the grid pick between them unpredictably.
    const rows = merchantRows.map((countRow) => {
      return {rowKey: countRow.rowKey, merchantName: countRow.merchantName, total: countRow.total || 0, forwardCount: countRow.forwardCount || 0, rvpCount: countRow.rvpCount || 0}
    })
    const totalsCount = finalMerchantShipmentCountData?.find((r) => r.merchantName === 'Totals') || {}
    rows.push({rowKey: 'Totals', merchantName: 'Totals', total: totalsCount.total || 0, forwardCount: totalsCount.forwardCount || 0, rvpCount: totalsCount.rvpCount || 0})
    return _.orderBy(rows.filter((r) => r.merchantName !== 'Totals'), 'total', 'desc').concat(rows.filter((r) => r.merchantName === 'Totals'))
  }, [merchantRows, finalMerchantShipmentCountData])

  // ── Chart data ────────────────────────────────────────────────────────────────
  const {trendData, merchantBarData, merchantRevBarData, courierPieData} = useMemo(() => {
    if (!finalMerchantShipmentCountData || !finalMerchantDateData) {
      return {trendData: [], merchantBarData: [], merchantRevBarData: [], courierPieData: []}
    }

    // Daily trend from Totals row — skip non-date keys
    const shipTotals = finalMerchantShipmentCountData.find((r) => r.merchantName === 'Totals') || {}
    const revTotals  = finalMerchantDateData.find((r) => r.merchantName === 'Totals') || {}

    const trend = Object.keys(shipTotals)
      .filter((k) => /^\d{4}-\d{2}-\d{2}$/.test(k))
      .sort()
      .map((dateKey) => {
        const [, m, d] = dateKey.split('-')
        return {
          date: `${d}/${m}`,
          Shipments: shipTotals[dateKey] || 0,
          Revenue: Math.round(revTotals[dateKey] || 0),
        }
      })

    // Top 10 merchants by shipments (horizontal bar)
    const topM = _.orderBy(merchantRows, 'total', 'desc').slice(0, 10).map((r) => ({
      name: r.merchantName.length > 18 ? r.merchantName.slice(0, 18) + '…' : r.merchantName,
      Shipments: r.total || 0,
    }))

    // Top 10 merchants by revenue
    const topMR = _.orderBy(revenueRows, 'total', 'desc').slice(0, 10).map((r) => ({
      name: r.merchantName.length > 18 ? r.merchantName.slice(0, 18) + '…' : r.merchantName,
      Revenue: Math.round(r.total || 0),
    }))

    // Courier pie — group by PARENT (roll up account codes under their courier parent),
    // so the split shows "XpressBees", "Delhivery"… not individual account codes.
    const parentRollup = _(courierRows)
      .groupBy((r) => r.parent || r.courierName)
      .map((rows, parent) => ({courierName: parent, total: _.sumBy(rows, 'total') || 0}))
      .value()
    const sortedCouriers = _.orderBy(parentRollup, 'total', 'desc')
    const grandTotal = _.sumBy(sortedCouriers, 'total') || 1
    let topN = Math.min(sortedCouriers.length, 8)
    while (topN < sortedCouriers.length) {
      const restSum = _.sumBy(sortedCouriers.slice(topN), 'total')
      if (restSum / grandTotal < 0.15) break
      topN++
    }
    const topSlice = sortedCouriers.slice(0, topN)
    const rest     = sortedCouriers.slice(topN)
    const pie = topSlice.map((r) => ({name: r.courierName || 'Unknown', value: r.total || 0}))
    if (rest.length) pie.push({name: 'Others', value: _.sumBy(rest, 'total')})

    return {trendData: trend, merchantBarData: topM, merchantRevBarData: topMR, courierPieData: pie}
  }, [finalMerchantShipmentCountData, finalMerchantDateData, merchantRows, revenueRows, courierRows])

  if (loading) return <Loader />

  const isCreated  = mode === 'overall' || mode === 'overall_today'
  const verb       = isCreated ? 'created' : 'delivered'
  const isToday    = mode === 'overall_today'
  const multiDay   = trendData.length > 1

  // ── Column builders ───────────────────────────────────────────────────────────
  function dateColumns(startDate, endDate, type = 'revenue') {
    const cols = []
    const cur = new Date(startDate)
    while (cur <= endDate) {
      const y = cur.getFullYear()
      const m = String(cur.getMonth() + 1).padStart(2, '0')
      const d = String(cur.getDate()).padStart(2, '0')
      const key = `${y}-${m}-${d}`
      cols.push({
        field: key,
        headerName: `${d}/${m}`,
        align: 'center', headerAlign: 'center',
        sortable: false, filterable: false,
        width: type === 'revenue' ? 110 : 90,
        renderCell: (params) => {
          /* Read the value off THIS row. It used to look the row back up in the source dataset by
             display name — but names are not unique (rows are grouped by merchant/courier id, and
             several ids resolve to the same name), so `find` returned whichever collided first and
             a row showed a different row's number. Visible on staging as a courier with TOTAL 519
             sitting next to a date value of 3,057, which a row's own data can never produce.
             The row already carries its date buckets, so no lookup is needed at all. */
          const val = params.row?.[params.field] || 0
          const display = val ? formatNumber(val.toFixed(0), 0) : '—'
          return (
            <Typography sx={{fontSize: '0.82rem', whiteSpace: 'nowrap', color: val ? '#1a1a1a' : '#bdbdbd'}}>
              {display}
            </Typography>
          )
        },
      })
      cur.setDate(cur.getDate() + 1)
    }
    return cols
  }

  function nameCol(field, label) {
    return {
      field, headerName: label, align: 'left', headerAlign: 'left',
      sortable: true, filterable: false, width: 180,
      renderCell: ({row}) => (
        <Tooltip title={row[field]} placement='top-start' arrow>
          <Typography sx={{
            fontSize: '0.82rem', fontWeight: row[field] === 'Totals' ? 800 : 600,
            color: row[field] === 'Totals' ? '#1565c0' : '#1a1a1a',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {row[field]}
          </Typography>
        </Tooltip>
      ),
    }
  }

  function totalCol(maxVal, grandTotal, prefix = '') {
    return {
      field: 'total', headerName: 'Total', align: 'left', headerAlign: 'center',
      sortable: true, filterable: false, width: 175,
      renderCell: ({row}) => {
        const nameKey = row.merchantName !== undefined ? 'merchantName' : 'courierName'
        return (
          <TotalCell
            value={row.total}
            maxVal={maxVal}
            grandTotal={grandTotal}
            isTotalsRow={row[nameKey] === 'Totals'}
            prefix={prefix}
          />
        )
      },
    }
  }

  const start = dateRange[0].startDate
  const end   = dateRange[0].endDate

  const brandCountCols   = [nameCol('merchantName', 'Merchant'), totalCol(maxMerchantShipments, totalShipments),    ...dateColumns(start, end, 'count')]
  const brandRevCols     = [nameCol('merchantName', 'Merchant'), totalCol(maxMerchantRevenue,   totalRevenue, '₹'), ...dateColumns(start, end, 'revenue')]
  const courierCountCols = [nameCol('courierName',  'Courier'),  totalCol(maxCourierShipments,  totalCourierShipments),    ...dateColumns(start, end, 'count')]
  const courierRevCols   = [nameCol('courierName',  'Courier'),  totalCol(maxCourierRevenue,    totalCourierRevenue, '₹'), ...dateColumns(start, end, 'revenue')]

  const combinedCols = [
    nameCol('merchantName', 'Merchant'),
    {
      field: 'total', headerName: 'Shipments', align: 'left', headerAlign: 'center',
      sortable: true, filterable: false, width: 175,
      renderCell: ({row}) => (
        <TotalCell value={row.total} maxVal={maxMerchantShipments} grandTotal={totalShipments} isTotalsRow={row.merchantName === 'Totals'} />
      ),
    },
    {
      field: 'forwardCount', headerName: 'FWD', align: 'center', headerAlign: 'center',
      sortable: true, filterable: false, flex: 1,
      renderCell: ({row}) => (
        <Chip
          label={formatNumber(row.forwardCount)}
          size='small'
          sx={{backgroundColor: row.merchantName === 'Totals' ? '#1565c0' : '#e3f2fd', color: row.merchantName === 'Totals' ? '#fff' : '#1565c0', fontWeight: 700, fontSize: '0.75rem', height: 22}}
        />
      ),
    },
    {
      field: 'rvpCount', headerName: 'RVP', align: 'center', headerAlign: 'center',
      sortable: true, filterable: false, flex: 1,
      renderCell: ({row}) => (
        <Chip
          label={formatNumber(row.rvpCount)}
          size='small'
          sx={{backgroundColor: row.merchantName === 'Totals' ? '#e65100' : '#fff3e0', color: row.merchantName === 'Totals' ? '#fff' : '#e65100', fontWeight: 700, fontSize: '0.75rem', height: 22}}
        />
      ),
    },
  ]

  const alertText = {
    overall:       'The revenue shown above represents the potential revenue from all shipments created in the selected date range, inclusive of GST. This is an estimated figure and does NOT reflect the actual billed revenue for the period, as Prozo bills only for shipments that are successfully delivered (including forward, RTO, and RVP deliveries). Please note that any cancelled shipments will further reduce the final billed revenue. This dashboard is designed primarily to help you track the daily shipment load, shipment revenue, and the ongoing revenue potential for ProShip.',
    overall_today: 'The revenue shown above represents the potential revenue from all shipments created in the selected date range, inclusive of GST. This is an estimated figure and does NOT reflect the actual billed revenue for the period, as Prozo bills only for shipments that are successfully delivered (including forward, RTO, and RVP deliveries). Please note that any cancelled shipments will further reduce the final billed revenue. This dashboard is designed primarily to help you track the daily shipment load, shipment revenue, and the ongoing revenue potential for ProShip. This data for shipments which have been successfully created is refreshed every 30 minutes.',
    realised:      'Realized revenue is the revenue (inclusive of GST) that will be billed to the brands. At Prozo, we bill all delivered shipments. These shipments could either be delivered to the end customers or shipments that are successfully RTO/RVP delivered. The realized revenue does NOT factor weight discrepancy which is under dispute or not accepted by the brand in Proship.',
  }

  return (
    <Grid container spacing={2}>

      {/* ── KPI Row 1 ── */}
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<StorefrontIcon sx={{fontSize: 22, color: '#1565c0'}} />}
          label='Active Merchants'
          value={formatNumber(activeMerchants)}
          sub={mode === 'overall_today' ? 'With shipments today' : 'In selected range'}
          color='#1565c0' bg='#e3f2fd'
          info={`Count of distinct merchants who have at least one shipment ${verb} in the selected date range.`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<LocalShippingIcon sx={{fontSize: 22, color: '#2e7d32'}} />}
          label='Total Shipments'
          value={formatNumber(totalShipments?.toFixed(0), 0)}
          sub={isCreated ? 'Created in selected range' : 'Delivered in selected range'}
          color='#2e7d32' bg='#e8f5e9'
          info={`Sum of all shipments ${verb} across all merchants and couriers in the selected date range.`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<AttachMoneyIcon sx={{fontSize: 22, color: '#6a1b9a'}} />}
          label={mode === 'realised' ? 'Realised Revenue' : 'Potential Revenue'}
          value={`₹${formatNumber(totalRevenue?.toFixed(0), 0)}`}
          sub='Inclusive of GST'
          color='#6a1b9a' bg='#f3e5f5'
          info={mode === 'realised'
            ? 'Sum of the shipment price (incl. GST) for all delivered shipments (forward + RTO/RVP).'
            : `Sum of the shipment price (incl. GST) for all shipments ${verb}. Prozo bills only on delivery.`}
        />
      </Grid>

      {/* ── KPI Row 2 ── */}
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<LocalOfferIcon sx={{fontSize: 22, color: '#e65100'}} />}
          label='Avg Revenue / Shipment'
          value={`₹${formatNumber(avgRevPerShipment.toFixed(0), 0)}`}
          sub='Per order average'
          color='#e65100' bg='#fff3e0'
          info='Calculated as: Total Revenue ÷ Total Shipments. Represents the average billing value per order.'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<LocalShippingIcon sx={{fontSize: 22, color: '#0277bd'}} />}
          label='Active Couriers'
          value={formatNumber(activeCouriers)}
          sub='Courier accounts used'
          color='#0277bd' bg='#e1f5fe'
          info={`Count of distinct courier accounts that processed at least one shipment in the selected date range.`}
        />
      </Grid>

      {/* ── Alert ── */}
      <Grid item xs={12}>
        <Alert
          severity={mode === 'realised' ? 'success' : 'info'}
          icon={<InfoOutlinedIcon fontSize='small' />}
          sx={{borderRadius: 2, fontSize: '0.8rem', py: 0.5, '& .MuiAlert-message': {lineHeight: 1.6}}}
        >
          <><strong>Important Notes</strong><br />{alertText[mode]}</>
        </Alert>
      </Grid>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/*  CHARTS SECTION                                                         */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      {/* ── Daily Trend (only when date range > 1 day) ── */}
      {multiDay && (
        <>
          <Grid item xs={12} md={6}>
            <ChartCard
              title='Daily Shipments Trend'
              sub={`Total shipments ${verb} per day across all merchants`}
              badge={`${trendData.length} days`}
            >
              <ResponsiveContainer width='100%' height={220}>
                <AreaChart data={trendData} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                  <defs>
                    <linearGradient id='shipGrad' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='5%' stopColor='#1565c0' stopOpacity={0.18} />
                      <stop offset='95%' stopColor='#1565c0' stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' vertical={false} />
                  <XAxis dataKey='date' tick={{fontSize: 11, fill: '#9e9e9e'}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize: 11, fill: '#9e9e9e'}} axisLine={false} tickLine={false} width={45}
                    tickFormatter={(v) => formatNumber(v)} />
                  <RTooltip content={<ChartTooltip />} />
                  <Area type='monotone' dataKey='Shipments' stroke='#1565c0' strokeWidth={2}
                    fill='url(#shipGrad)' dot={false} activeDot={{r: 4, fill: '#1565c0'}} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard
              title={mode === 'realised' ? 'Daily Realised Revenue' : 'Daily Revenue Trend'}
              sub='Total revenue (incl. GST) per day across all merchants'
              badge={`₹${formatNumber(totalRevenue?.toFixed(0), 0)}`}
            >
              <ResponsiveContainer width='100%' height={220}>
                <AreaChart data={trendData} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                  <defs>
                    <linearGradient id='revGrad' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='5%' stopColor='#6a1b9a' stopOpacity={0.18} />
                      <stop offset='95%' stopColor='#6a1b9a' stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' vertical={false} />
                  <XAxis dataKey='date' tick={{fontSize: 11, fill: '#9e9e9e'}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize: 11, fill: '#9e9e9e'}} axisLine={false} tickLine={false} width={55}
                    tickFormatter={(v) => `₹${formatNumber(v)}`} />
                  <RTooltip content={<ChartTooltip prefix='₹' />} />
                  <Area type='monotone' dataKey='Revenue' stroke='#6a1b9a' strokeWidth={2}
                    fill='url(#revGrad)' dot={false} activeDot={{r: 4, fill: '#6a1b9a'}} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </>
      )}

      {/* ── Top Merchants by Shipments (horizontal bar) ── */}
      {merchantBarData.length > 0 && (
        <Grid item xs={12} md={isToday ? 6 : 7}>
          <ChartCard
            title='Top Merchants — Shipments'
            sub={`Top ${merchantBarData.length} merchants by shipment count`}
            badge={`${activeMerchants} total`}
          >
            <ResponsiveContainer width='100%' height={Math.max(220, merchantBarData.length * 32)}>
              <BarChart
                data={merchantBarData}
                layout='vertical'
                margin={{top: 0, right: 16, left: 0, bottom: 0}}
                barSize={14}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' horizontal={false} />
                <XAxis type='number' tick={{fontSize: 11, fill: '#9e9e9e'}} axisLine={false} tickLine={false}
                  tickFormatter={(v) => formatNumber(v)} />
                <YAxis type='category' dataKey='name' width={130} tick={{fontSize: 11, fill: '#424242'}}
                  axisLine={false} tickLine={false} interval={0} />
                <RTooltip content={<ChartTooltip />} />
                <Bar dataKey='Shipments' radius={[0, 4, 4, 0]}>
                  {merchantBarData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      )}

      {/* ── Courier Distribution (donut) ── */}
      {courierPieData.length > 0 && (
        <Grid item xs={12} md={isToday ? 6 : 5}>
          <ChartCard
            title='Courier Split — Shipments'
            sub='Share of shipments by courier account'
            badge={`${activeCouriers} couriers`}
          >
            <ResponsiveContainer width='100%' height={Math.max(220, merchantBarData.length * 32)}>
              <PieChart>
                <Pie
                  data={courierPieData}
                  cx='50%' cy='50%'
                  innerRadius='48%'
                  outerRadius='72%'
                  paddingAngle={2}
                  dataKey='value'
                  nameKey='name'
                >
                  {courierPieData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(value, name) => [formatNumber(Number(value).toFixed(0), 0), name]}
                  contentStyle={{fontSize: 12, borderRadius: 8, border: '1px solid #e0e0e0'}}
                />
                <Legend
                  iconType='circle' iconSize={8}
                  wrapperStyle={{fontSize: 11, paddingTop: 8}}
                  formatter={(value) => value.length > 20 ? value.slice(0, 20) + '…' : value}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      )}

      {/* ── Today's view: merchant revenue bar ── */}
      {isToday && merchantRevBarData.length > 0 && (
        <Grid item xs={12}>
          <ChartCard
            title='Top Merchants — Revenue (Potential)'
            sub='Revenue (incl. GST) per merchant for today'
            badge={`₹${formatNumber(totalRevenue?.toFixed(0), 0)}`}
          >
            <ResponsiveContainer width='100%' height={220}>
              <BarChart
                data={merchantRevBarData}
                margin={{top: 4, right: 16, left: 0, bottom: 0}}
                barSize={28}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' vertical={false} />
                <XAxis dataKey='name' tick={{fontSize: 11, fill: '#424242'}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize: 11, fill: '#9e9e9e'}} axisLine={false} tickLine={false} width={70}
                  tickFormatter={(v) => `₹${formatNumber(v)}`} />
                <RTooltip content={<ChartTooltip prefix='₹' />} />
                <Bar dataKey='Revenue' radius={[4, 4, 0, 0]}>
                  {merchantRevBarData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/*  TABLES SECTION                                                         */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      {mode === 'overall_today' ? (
        <Grid item xs={12}>
          <TableSection
            title='Merchant Summary — Shipments (FWD / RVP)'
            sub='Shipment count per merchant for today with FWD and RVP bifurcation. Bar = share of top merchant. % = share of total.'
            badge={`${activeMerchants} merchants`}
            info="Each row shows a merchant's total shipments for today, split into Forward (FWD) and Reverse Pickup (RVP) counts. The bar in the Shipments column is proportional to the highest individual merchant value. The % is the share of the grand total."
            columns={combinedCols}
            rows={combinedRows}
            nameKey='merchantName'
          />
        </Grid>
      ) : (
        <>
          <Grid item xs={12}>
            <TableSection
              title='Merchant Shipment Count'
              sub={`Shipments ${verb} per merchant per day. Bar = share of top merchant. % = share of total.`}
              badge={`${activeMerchants} merchants`}
              info='Each row is one merchant. The Total column shows a bar proportional to the highest merchant and the % share of the grand total. Date columns show daily counts.'
              columns={brandCountCols}
              rows={_.orderBy(finalMerchantShipmentCountData, 'total', 'desc') || []}
              nameKey='merchantName'
            />
          </Grid>
          <Grid item xs={12}>
            <TableSection
              title={mode === 'realised' ? 'Merchant Realised Revenue' : 'Merchant Revenue (Potential)'}
              sub={`Revenue (incl. GST) from shipments ${verb} per merchant per day. RTO revenue excluded.`}
              badge={`₹${formatNumber(totalRevenue?.toFixed(0), 0)} total`}
              info='Each row is one merchant. The Total column shows a bar proportional to the highest revenue merchant and the % share of grand total revenue.'
              columns={brandRevCols}
              rows={_.orderBy(finalMerchantDateData, 'total', 'desc') || []}
              nameKey='merchantName'
            />
          </Grid>
        </>
      )}

      {/* ── Courier tables (access-gated) ── */}
      {accessList.includes('DASHBOARD_COURIER_LEVEL_DATA') && (
        <>
          <Grid item xs={12}>
            <TableSection
              title='Courier Shipment Count'
              sub={`Shipments ${verb} per courier per day.`}
              badge={`${activeCouriers} couriers`}
              columns={courierCountCols}
              rows={_.orderBy(finalCourierShipmentCountData, 'total', 'desc') || []}
              nameKey='courierName'
            />
          </Grid>
          <Grid item xs={12}>
            <TableSection
              title='Courier Revenue'
              sub={`Revenue (incl. GST) from shipments ${verb} per courier per day. RTO excluded.`}
              columns={courierRevCols}
              rows={_.orderBy(finalCourierDateData, 'total', 'desc') || []}
              nameKey='courierName'
            />
          </Grid>
        </>
      )}
    </Grid>
  )
}
